import React from "react";
import {
    createBrowserRouter,
    Navigate
} from "react-router-dom";

import RequireAuth from "./auth/RequireAuth";
import MainLayout from "./layouts/MainLayout";
import SnackbarError from "./components/SnackbarError";

// Pages
import AuthLayout from "./layouts/AuthLayout";
import UnauthorizedRole from "./pages/auth/UnauthorizedRole";
import SignIn from "./pages/auth/SignIn";
import ForgotPassword from "./pages/auth/ForgotPassword";
import PasswordReset from "./pages/auth/PasswordReset";
import EmailConfirmation from "./pages/auth/EmailConfirmation";

import Workgroups from "./pages/Workgroups";
import Workgroup from "./pages/Workgroups/Workgroup";
import WorkgroupCreate from "./pages/Workgroups/WorkgroupCreate";
import Users from "./pages/Users";
import User from "./pages/User";
import UserCreate from "./pages/Users/UserCreate";
import UserAccount from "./pages/User/UserAccount";
import Groups from "./pages/Groups";
import GroupCreate from "./pages/Groups/GroupCreate";
import Group from "./pages/Groups/Group";

import Dashboard from "./pages/Dashboard";
import Stations from "./pages/Stations";
import Station from "./pages/Stations/Station";
import StationCreate from "./pages/Stations/StationCreate";
import Models1 from "./pages/Models1";
import Model1Create from "./pages/Models1/Model1Create";
import Model1Edit from "./pages/Models1/Model1Edit";
import Models23 from "./pages/Model23";
import Model23Create from "./pages/Model23/Model23Create";
import Model23Edit from "./pages/Model23/Model23Edit";
import ImportTasks from "./pages/ImportTasks";
import ImportTask from "./pages/ImportTasks/ImportTask";
import ImportTaskCreate from "./pages/ImportTasks/ImportTaskCreate";
import Incident from "./pages/Incidents";
import IncidentCreate from "./pages/Incidents/IncidentCreate";
import IncidentEdit from "./pages/Incidents/IncidentEdit";
import IncidentPre2021Edit from "./pages/Incidents/IncidentPre2021Edit";


// Public pages
import PublicLayout from "./layouts/PublicLayout";
import PublicIncident from "./pages/public/Incidents";
import Models4 from "./pages/Models4";
import Analytics from "./pages/Analytics";
import IncidentAnalytics from "./pages/public/Incidents/IncidentAnalytics";


const router = createBrowserRouter([
    {
        path: "/auth",
        element: <AuthLayout/>, // <AuthLayout /> is a component that renders the layout
        errorElement: <SnackbarError/>,
        handle: {
            crumb: () => `Home`,
        },
        children: [
            {
                path: "sign-in",
                element: <SignIn/>,
            },
            {
                path: "forgot-password",
                element: <ForgotPassword/>,
            },
            {
                path: "reset-password",
                element: <PasswordReset/>,
            },
            {
                path: "email-confirmation",
                element: <EmailConfirmation/>,
            },
            {
                path: "*",
                element: <Navigate to="/auth/sign-in"/>,
            },
        ]
    },
    {
        element: <RequireAuth><MainLayout/></RequireAuth>,
        errorElement: <SnackbarError/>,
        children: [
            {
                path: "/dashboard",
                element: <Dashboard/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `dashboard`,
                },
            },
            {
                path: "/models1",
                element: <Models1/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `models1`,
                },
                children: [
                    {
                        path: "new",
                        element: <Model1Create/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newModel1`,
                        }
                    },
                    {
                        path: ":model1Id/edit",
                        element: <Model1Edit/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `editModel1`,
                        }

                    }
                ]
            },
            {
                path: "/models23",
                element: <Models23/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `models23`,
                },
                children: [
                    {
                        path: "new",
                        element: <Model23Create/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newModel23`,
                        }
                    },
                    {
                        path: "models4",
                        element: <Models4/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `model4`,
                        }
                    },
                    {
                        path: ":model23Id/edit",
                        element: <Model23Edit/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `editModel23`,
                        }

                    }
                ]
            },
            {
                path: "/stations",
                element: <Stations/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `stations`,
                },
                children: [
                    {
                        path: "new",
                        element: <StationCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newStation`,
                        }
                    },
                    {
                        path: ":stationId",
                        element: <Station/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `station`,
                        }
                    }
                ]
            },
            {
                path: "/incidents",
                element: <Incident/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `incidents`,
                },
                children: [
                    {
                        path: "new",
                        element: <IncidentCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newIncident`,
                        }
                    },
                    {
                        path: ":incidentId/edit",
                        element: <IncidentEdit/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `editIncident`,
                        }
                    },
                    {
                        path: "pre2021/:incidentId/edit",
                        element: <IncidentPre2021Edit/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `editIncident`,
                        }
                    }
                ]
            },
            {
                path: "analytics",
                element: <Analytics />,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `analytics`,
                },
            },
            {
                path: "/users/me",
                element: <UserAccount/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `account`,
                }
            },
            {
                path: "/users",
                element: <Users/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `users`,
                },
                children: [
                    {
                        path: "new",
                        element: <UserCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newUser`,
                        }
                    },
                    {
                        path: ":userId",
                        element: <User/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `user`,
                        }
                    }
                ],
            },
            {
                path: "/groups",
                element: <Groups/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `groups`,
                },
                children: [
                    {
                        path: "new",
                        element: <GroupCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newGroup`,
                        }
                    },
                    {
                        path: ":groupId",
                        element: <Group/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `group`,
                        }
                    }
                ]
            },
            {
                path: "/importTasks",
                element: <ImportTasks/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `importTasks`,
                },
                children: [
                    {
                        path: "new",
                        element: <ImportTaskCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newImportTask`,
                        }
                    },
                    {
                        path: ":importTaskId",
                        element: <ImportTask/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `importTask`,
                        }
                    }
                ]
            },
            {
                path: "/workgroups",
                element: <Workgroups/>,
                errorElement: <SnackbarError/>,
                handle: {
                    crumb: `workgroups`,
                },
                children: [
                    {
                        path: "new",
                        element: <WorkgroupCreate/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `newWorkgroup`,
                        }
                    },
                    {
                        path: ":workgroupId",
                        element: <Workgroup/>,
                        errorElement: <SnackbarError/>,
                        handle: {
                            crumb: `workgroup`,
                        }
                    }
                ]
            },
            {
                path: "/unauthorizedRole",
                element: <UnauthorizedRole/>,
            }
        ]
    },
    {
        element: <PublicLayout/>,
        errorElement: <SnackbarError/>,
        path: "/public",
        children: [
            {
                path: "incidents",
                element: <PublicIncident />,
                errorElement: <SnackbarError/>,
                children: [
                    {
                        path: "analytics",
                        element: <IncidentAnalytics/>,
                        errorElement: <SnackbarError/>
                    }
                ]
            },
        ]
    },
    {
        path: "*",
        element: <Navigate to="/dashboard"/>,
    }
]);

export default router;